import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from '../../layouts/Header';
import { Card, CardBody, CardTitle, Row, Col, Input, Label, Modal, ModalHeader, ModalBody } from 'reactstrap';
import { Button, Badge, Form } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import DataTable from 'react-data-table-component';
import ConfirmModal from '../../components/ConfirmModal';
import DataTableSkeleton from '../../components/DataTableSkeleton';
import OrderItemsCell, {
    getOrderItemName,
    getOrderItemImage,
    PRODUCT_IMAGE_PLACEHOLDER
} from '../../components/OrderItemsCell';
import Footer from '../../layouts/Footer';
import OrderActionsMenu from '../../components/admin/OrderActionsMenu';
import OrderPaymentBadges from '../../components/admin/OrderPaymentBadges';
import { dataTableWideStyles, dataTablePaginationOptions } from '../../components/admin/dataTableConfig';
import moment from 'moment';
import {
    getAllOrders,
    getAdminOrderById,
    updateOrderStatus,
    deleteOrder,
    updateBulkOrderStatus,
    deleteOrders,
    recordAdminShipment,
    formatCurrency,
    computeOrderStatistics,
    normalizeOrderListSummary,
    filterOrdersForClientSearch,
    getStatusBadgeColor,
    getPaymentDisplayForOrder,
    getOrderStatuses,
    getPaymentStatuses,
    getPaymentMethods,
    canVerifyCashfreePayment,
    hasFulfillmentPaymentMismatch,
    isCashfreeUnverifiedPayment
} from '../../helper/order_helper';
import { formatOrderItemSku } from '../../helper/order_item_helper';
import {
    getOrderItemUnitPrice,
    itemShowsMrpStrike,
    getOrderItemMrp,
} from '../../helper/order_summary_helper';
import OrderDetailShippingCard from '../../components/admin/OrderDetailShippingCard';
import OrderDetailSummaryCard from '../../components/admin/OrderDetailSummaryCard';
import {
    verifyCashfreePaymentAdmin,
    getCashfreeVerifyMessage
} from '../../helper/payment_helper';
import {
    createShipcluesShipmentFromOrder,
    readShipcluesRecordIds,
    getShipcluesErrorMessage
} from '../../helper/shipclues_helper';
import {
    canDownloadInvoice,
    canIssueInvoice,
    downloadInvoiceByOrderId,
    issueInvoiceForOrder,
} from '../../helper/invoice_helper';
import AdminRowActions, { AdminActionButton } from '../../components/admin/AdminRowActions';
import AdminEmptyState from '../../components/admin/AdminEmptyState';
import OrderShipmentTracking from '../../components/admin/OrderShipmentTracking';
import OrderShipmentStatusChip from '../../components/admin/OrderShipmentStatusChip';
import OrderShipmentListCell from '../../components/admin/OrderShipmentListCell';

function OrderDetailsContent({
    data,
    onVerifyCashfree,
    verifyingCashfreeId,
    onDownloadInvoice,
    onIssueInvoice,
    downloadingInvoiceId,
    issuingInvoiceId,
}) {
    const orderItems = data?.items || [];
    const shippingAddress = data?.shippingAddress || data?.address || {};
    const paymentDisplay = getPaymentDisplayForOrder(data);
    const showMismatchWarning = hasFulfillmentPaymentMismatch(data);
    const showVerifyAction = canVerifyCashfreePayment(data);
    const isVerifying = verifyingCashfreeId === data?._id;
    const canDownload = canDownloadInvoice(data);
    const canIssue = canIssueInvoice(data);
    const isDownloading = downloadingInvoiceId === data?._id;
    const isIssuing = issuingInvoiceId === data?._id;

    return (
        <div>
            {showMismatchWarning && (
                <div className="alert alert-danger d-flex align-items-start gap-2 mb-3" role="alert">
                    <i className="ri-error-warning-fill fs-5 flex-shrink-0 mt-1" />
                    <div>
                        <strong>Payment not verified</strong>
                        <p className="mb-0 small">
                            This order is <strong>{data?.status}</strong> but Cashfree payment is still unconfirmed.
                            Verify payment before fulfilling further or issuing refunds.
                        </p>
                    </div>
                </div>
            )}
            <Row className="g-3 mb-3">
                <Col sm={6}>
                    <span className="text-muted small">Order #</span>
                    <div className="fw-semibold">{data?.orderNumber}</div>
                </Col>
                <Col sm={6}>
                    <span className="text-muted small">Date</span>
                    <div className="fw-semibold">
                        {data?.createdAt ? moment(data.createdAt).format('DD MMM YYYY, hh:mm A') : '—'}
                    </div>
                </Col>
                <Col sm={6}>
                    <span className="text-muted small">Tax invoice</span>
                    <div className="fw-semibold small">
                        {data?.invoiceNumber || (
                            <span className="text-muted">Not issued yet</span>
                        )}
                    </div>
                    {data?.invoiceDate ? (
                        <small className="text-muted">
                            {moment(data.invoiceDate).format('DD MMM YYYY')}
                        </small>
                    ) : null}
                    <div className="mt-2 d-flex flex-wrap gap-2">
                        {onDownloadInvoice ? (
                            <Button
                                variant="outline-success"
                                size="sm"
                                disabled={!canDownload || isDownloading}
                                title={
                                    canDownload
                                        ? 'Download GST tax invoice PDF'
                                        : 'Available after payment or delivery'
                                }
                                onClick={() => onDownloadInvoice(data)}
                            >
                                {isDownloading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                                        Downloading…
                                    </>
                                ) : (
                                    <>
                                        <i className="ri-file-download-line me-1" />
                                        Download invoice
                                    </>
                                )}
                            </Button>
                        ) : null}
                        {onIssueInvoice && canIssue ? (
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                disabled={isIssuing}
                                onClick={() => onIssueInvoice(data)}
                            >
                                {isIssuing ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                                        Issuing…
                                    </>
                                ) : (
                                    <>
                                        <i className="ri-file-list-3-line me-1" />
                                        Issue invoice
                                    </>
                                )}
                            </Button>
                        ) : null}
                    </div>
                </Col>
                <Col sm={6}>
                    <span className="text-muted small">Shipment status</span>
                    <div className="mt-1">
                        <OrderShipmentStatusChip order={data} />
                    </div>
                </Col>
                <Col sm={6}>
                    <span className="text-muted small">Status</span>
                    <div>
                        <Badge bg={getStatusBadgeColor(data?.status)}>{data?.status}</Badge>
                    </div>
                </Col>
                <Col sm={6}>
                    <span className="text-muted small">Payment</span>
                    <div className="d-flex gap-1 flex-wrap align-items-center">
                        <Badge bg="secondary">{data?.paymentMethod}</Badge>
                        <Badge bg={paymentDisplay.statusBadge} title={paymentDisplay.title || undefined}>
                            {paymentDisplay.statusLabel}
                        </Badge>
                        {showVerifyAction && onVerifyCashfree ? (
                            <Button
                                variant="outline-warning"
                                size="sm"
                                className="ms-1"
                                disabled={isVerifying}
                                onClick={() => onVerifyCashfree(data)}
                            >
                                {isVerifying ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                                        Verifying…
                                    </>
                                ) : (
                                    <>
                                        <i className="ri-shield-check-line me-1" />
                                        Verify Cashfree
                                    </>
                                )}
                            </Button>
                        ) : null}
                    </div>
                    {data?.cashfreeOrderId ? (
                        <div className="mt-2 p-2 bg-light rounded small">
                            <div className="text-muted">Cashfree Order ID</div>
                            <div style={{ wordBreak: 'break-all' }}>{data.cashfreeOrderId}</div>
                            {data?.cashfreePaymentId ? (
                                <>
                                    <div className="text-muted mt-1">Cashfree Payment ID</div>
                                    <div style={{ wordBreak: 'break-all' }}>{data.cashfreePaymentId}</div>
                                </>
                            ) : (
                                <div className="text-danger mt-1">Payment ID not recorded — not confirmed in database</div>
                            )}
                        </div>
                    ) : null}
                </Col>
            </Row>
            <Row>
                <Col xs={12}>
                    <OrderShipmentTracking order={data} />
                </Col>
            </Row>
            <Row>
                <Col md={6}>
                    <h6 className="mb-3 fw-bold">
                        <i className="ri-shopping-bag-line me-1" />
                        Order Items ({orderItems.length})
                    </h6>
                    {orderItems.length > 0 ? (
                        <div className="order-items-cell" style={{ maxHeight: '360px', overflowY: 'auto' }}>
                            {orderItems.map((item, index) => (
                                <div className="order-item-row" key={index}>
                                    <span className="order-item-seq">{String(index + 1).padStart(2, '0')}</span>
                                    <img
                                        src={getOrderItemImage(item) || PRODUCT_IMAGE_PLACEHOLDER}
                                        alt={getOrderItemName(item)}
                                        className="order-item-thumb"
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = PRODUCT_IMAGE_PLACEHOLDER;
                                        }}
                                    />
                                    <div className="order-item-body">
                                        <p className="order-item-name">{getOrderItemName(item)}</p>
                                        <p className="order-item-meta">
                                            SKU: <strong>{formatOrderItemSku(item)}</strong>
                                            {' · '}
                                            Size: <strong>{item.size || 'N/A'}</strong> · Qty: <strong>{item.quantity}</strong>
                                        </p>
                                        <p className="order-item-meta mb-0 order-amount">
                                            <strong>{formatCurrency(getOrderItemUnitPrice(item) * (item.quantity || 1))}</strong>
                                            <span className="text-muted">
                                                {' '}
                                                (
                                                {itemShowsMrpStrike(item) ? (
                                                    <>
                                                        <span className="text-decoration-line-through me-1">
                                                            {formatCurrency(getOrderItemMrp(item))}
                                                        </span>
                                                    </>
                                                ) : null}
                                                {formatCurrency(getOrderItemUnitPrice(item))} each)
                                            </span>
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-muted">No items in this order</p>
                    )}
                </Col>
                <Col md={6}>
                    <OrderDetailShippingCard shippingAddress={shippingAddress} />
                    <OrderDetailSummaryCard order={data} />
                </Col>
            </Row>
        </div>
    );
}

function Order() {
    const user = useSelector(state => state.user);
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [columns, setColumns] = useState([]);
    const [statusUpdate, setStatusUpdate] = useState({ show: false, orderId: null, currentStatus: '', newStatus: '' });
    const [bulkStatusUpdate, setBulkStatusUpdate] = useState({ show: false, newStatus: 'Pending' });
    const [deleteModal, setDeleteModal] = useState({ show: false, orderIds: [], label: '' });
    const [selectedRows, setSelectedRows] = useState([]);
    const [toggleCleared, setToggleCleared] = useState(false);
    const [creatingShipmentId, setCreatingShipmentId] = useState(null);
    const [createdShipments, setCreatedShipments] = useState({});
    const [viewOrder, setViewOrder] = useState(null);
    const [viewOrderLoading, setViewOrderLoading] = useState(false);
    const [verifyingCashfreeId, setVerifyingCashfreeId] = useState(null);
    const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);
    const [issuingInvoiceId, setIssuingInvoiceId] = useState(null);
    const [isInitialMount, setIsInitialMount] = useState(true);

    // Pagination and Filters
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalOrders: 0
    });
    const [filters, setFilters] = useState({
        page: 1,
        limit: 20,
        search: searchParams.get('search') || '',
        status: '',
        paymentStatus: '',
        paymentMethod: '',
        startDate: '',
        endDate: ''
    });

    // Statistics
    const [statistics, setStatistics] = useState({
        totalOrders: 0,
        totalRevenue: 0,
        averageOrderValue: 0,
        gmvOrders: 0,
        cancelledOrders: 0,
        cancelledAmount: 0,
        collectedRevenue: null,
        pendingCollection: null,
    });

    const orderStatuses = getOrderStatuses();
    const paymentStatuses = getPaymentStatuses();
    const paymentMethods = getPaymentMethods();

    useEffect(() => {
        loadColumns();
        getData();
        setIsInitialMount(false);
    }, []);

    useEffect(() => {
        loadColumns();
    }, [creatingShipmentId, createdShipments, verifyingCashfreeId, downloadingInvoiceId, issuingInvoiceId]);

    // Refetch data when filters change (but not on initial mount)
    useEffect(() => {
        if (isInitialMount) return; // Skip on initial mount

        const timeoutId = setTimeout(() => {
            getData();
        }, 300); // Debounce to prevent too many calls

        return () => clearTimeout(timeoutId);
    }, [
        filters.page,
        filters.limit,
        filters.search,
        filters.status,
        filters.paymentStatus,
        filters.paymentMethod,
        filters.startDate,
        filters.endDate
    ]);

    const loadColumns = () => {
        setColumns([
            {
                name: 'Products',
                selector: row => row.items?.map(item => getOrderItemName(item)).join(', '),
                sortable: false,
                width: '320px',
                cell: row => <OrderItemsCell items={row.items} maxVisible={3} />
            },
            {
                name: 'Order #',
                selector: row => row.orderNumber,
                sortable: true,
                width: '160px',
                cell: row => (
                    <div className="order-number-cell">
                        <div style={{ fontWeight: 'bold', fontSize: '12px' }}>
                            {row.orderNumber}
                        </div>
                        <small className="text-muted">
                            {moment(row.createdAt).format('DD MMM YYYY, hh:mm A')}
                        </small>
                    </div>
                )
            },
            {
                name: 'Customer',
                selector: row => row.shippingAddress?.firstName,
                sortable: true,
                width: '180px',
                cell: row => (
                    <div>
                        <div style={{ fontWeight: '500' }}>
                            {row.shippingAddress?.firstName} {row.shippingAddress?.lastName}
                        </div>
                        <small className="text-muted">{row.shippingAddress?.email}</small>
                    </div>
                )
            },
            {
                name: 'Total Amount',
                selector: row => row.total || row.amount,
                sortable: true,
                width: '130px',
                cell: row => (
                    <strong className="order-amount">
                        {formatCurrency(row.total || row.amount)}
                    </strong>
                )
            },
            {
                name: 'Status',
                selector: row => row.status,
                sortable: true,
                width: '150px',
                cell: row => (
                    <Badge bg={getStatusBadgeColor(row.status)}>
                        {row.status}
                    </Badge>
                )
            },
            {
                name: 'Payment',
                selector: row => row.paymentMethod,
                sortable: true,
                width: '150px',
                cell: row => <OrderPaymentBadges order={row} compact />
            },
            {
                name: 'Shipment',
                selector: row => row.shipmentStatus,
                sortable: true,
                width: '200px',
                minWidth: '180px',
                grow: 1,
                cell: row => <OrderShipmentListCell order={row} />
            },
            {
                name: 'Invoice',
                selector: row => row.invoiceNumber,
                sortable: true,
                width: '160px',
                cell: row => (
                    <div className="small">
                        {row.invoiceNumber ? (
                            <>
                                <div className="fw-semibold" style={{ wordBreak: 'break-all' }}>
                                    {row.invoiceNumber}
                                </div>
                                {row.invoiceDate ? (
                                    <small className="text-muted">
                                        {moment(row.invoiceDate).format('DD MMM YYYY')}
                                    </small>
                                ) : null}
                            </>
                        ) : (
                            <span className="text-muted">—</span>
                        )}
                    </div>
                )
            },
            {
                name: 'Actions',
                width: '110px',
                minWidth: '110px',
                ignoreRowClick: true,
                allowOverflow: true,
                button: true,
                cell: row => {
                    const isCreating = creatingShipmentId === row._id;
                    const isAlreadyCreated = Boolean(
                        row.shipcluesOrderId ||
                        row.shipcluesShipmentId ||
                        createdShipments[row._id]
                    );

                    return (
                        <OrderActionsMenu
                            row={row}
                            isCreating={isCreating}
                            isAlreadyCreated={isAlreadyCreated}
                            isVerifyingCashfree={verifyingCashfreeId === row._id}
                            canVerifyCashfree={canVerifyCashfreePayment(row)}
                            canDownloadInvoice={canDownloadInvoice(row)}
                            canIssueInvoice={canIssueInvoice(row)}
                            isDownloadingInvoice={downloadingInvoiceId === row._id}
                            isIssuingInvoice={issuingInvoiceId === row._id}
                            onView={handleViewOrder}
                            onStatus={handleStatusChange}
                            onShip={handleCreateShipment}
                            onVerifyCashfree={handleVerifyCashfreePayment}
                            onDownloadInvoice={handleDownloadInvoice}
                            onIssueInvoice={handleIssueInvoice}
                            onDelete={(r) => handleDeleteClick([r._id], r.orderNumber)}
                        />
                    );
                }
            }
        ]);
    };

    const getData = async () => {
        if (loading) return null; // Prevent multiple simultaneous calls

        setLoading(true);
        try {
            const params = {};

            // Only add non-empty filter values
            if (filters.page) params.page = filters.page;
            if (filters.limit) params.limit = filters.limit;
            if (filters.search) params.search = filters.search;
            if (filters.status) params.status = filters.status;
            if (filters.paymentStatus) params.paymentStatus = filters.paymentStatus;
            if (filters.paymentMethod) params.paymentMethod = filters.paymentMethod;
            if (filters.startDate) params.startDate = filters.startDate;
            if (filters.endDate) params.endDate = filters.endDate;

            console.log('Fetching orders with params:', params);

            const response = await getAllOrders(user?.token, params);

            console.log('API Response:', response);

            if (response?.success) {
                // Handle different response structures
                let orders = response.orders || response.data?.orders || [];
                if (filters.paymentMethod) {
                    orders = orders.filter(
                        (order) => String(order?.paymentMethod || '').toLowerCase() === filters.paymentMethod.toLowerCase()
                    );
                }
                if (filters.search) {
                    orders = filterOrdersForClientSearch(orders, filters.search);
                }
                setData(orders);

                // Update pagination
                if (response.data?.pagination) {
                    setPagination(response.data.pagination);
                } else if (response.pagination) {
                    setPagination(response.pagination);
                } else {
                    setPagination({
                        currentPage: filters.page,
                        totalPages: 1,
                        totalOrders: orders.length
                    });
                }

                const apiSummary = normalizeOrderListSummary(response);
                if (apiSummary) {
                    setStatistics(apiSummary);
                } else {
                    const apiStats = response.data?.statistics || response.statistics;
                    const computedStats = computeOrderStatistics(orders, response.data?.pagination || response.pagination || {
                        totalOrders: orders.length,
                    });
                    setStatistics(apiStats || computedStats);
                }

                // Only show success message if we have orders
                if (orders.length === 0) {
                    console.log('No orders found');
                }
                return orders;
            } else {
                toast.error(response?.message || 'Failed to fetch orders');
                setData([]);
                return [];
            }
        } catch (error) {
            console.error('Error fetching orders:', error);
            toast.error('Error loading orders');
            setData([]);
            return [];
        } finally {
            setLoading(false);
        }
    };

    const patchOrderInList = (orderId, fields) => {
        if (!orderId || !fields) return;
        setData((prev) =>
            prev.map((row) => (row._id === orderId ? { ...row, ...fields } : row))
        );
        setViewOrder((prev) =>
            prev?._id === orderId ? { ...prev, ...fields } : prev
        );
    };

    const handleViewOrder = async (row) => {
        if (!row?._id) {
            toast.error('Invalid order selected');
            return;
        }

        setViewOrder(row);
        setViewOrderLoading(true);
        try {
            const response = await getAdminOrderById(row._id, user?.token);
            if (response?.success) {
                const order = response.data?.order || response.order;
                if (order) {
                    setViewOrder(order);
                } else {
                    toast.warning('Order details were empty');
                }
            } else {
                toast.error(response?.message || 'Order not found');
            }
        } catch (error) {
            console.error('Error loading order details:', error);
            toast.error('Failed to load order details');
        } finally {
            setViewOrderLoading(false);
        }
    };

    const handleStatusChange = (order) => {
        setStatusUpdate({
            show: true,
            orderId: order._id,
            currentStatus: order.status,
            newStatus: order.status
        });
    };

    const handleDownloadInvoice = async (order) => {
        if (!order?._id) {
            toast.error('Invalid order selected');
            return;
        }
        if (!canDownloadInvoice(order)) {
            toast.warning('Invoice is available after payment is completed or order is delivered.');
            return;
        }
        if (downloadingInvoiceId) return;

        setDownloadingInvoiceId(order._id);
        try {
            let orderForPdf = order;
            if (!orderForPdf.items?.length) {
                const detail = await getAdminOrderById(order._id, user?.token);
                orderForPdf =
                    detail?.data?.order ||
                    detail?.order ||
                    detail?.data ||
                    orderForPdf;
            }

            const result = await downloadInvoiceByOrderId(order._id, {
                invoiceNumber: orderForPdf.invoiceNumber || order.invoiceNumber,
                orderNumber: orderForPdf.orderNumber || order.orderNumber,
                token: user?.token,
                order: orderForPdf,
            });
            if (result?.fallback) {
                toast.info('No server invoice found (404). Downloaded client-generated PDF.');
            } else {
                toast.success('Invoice PDF downloaded');
            }
            const refreshedOrders = await getData();
            if (viewOrder?._id === order._id && refreshedOrders?.length) {
                const refreshed = refreshedOrders.find((row) => row._id === order._id);
                if (refreshed) setViewOrder(refreshed);
            }
        } catch (error) {
            toast.error(error?.message || 'Failed to download invoice');
        } finally {
            setDownloadingInvoiceId(null);
        }
    };

    const handleIssueInvoice = async (order) => {
        if (!order?._id) {
            toast.error('Invalid order selected');
            return;
        }
        if (!canIssueInvoice(order)) {
            toast.warning('Order is not eligible for invoice issue yet.');
            return;
        }
        if (issuingInvoiceId) return;

        setIssuingInvoiceId(order._id);
        try {
            const result = await issueInvoiceForOrder(order._id, user?.token);
            if (result?.success === false) {
                toast.error(result?.message || 'Failed to issue invoice');
                return;
            }
            toast.success(result?.message || 'Invoice issued');
            const refreshedOrders = await getData();
            if (viewOrder?._id === order._id && refreshedOrders?.length) {
                const refreshed = refreshedOrders.find((row) => row._id === order._id);
                if (refreshed) setViewOrder(refreshed);
            }
        } catch (error) {
            toast.error(error?.message || 'Failed to issue invoice');
        } finally {
            setIssuingInvoiceId(null);
        }
    };

    const handleVerifyCashfreePayment = async (order) => {
        if (!order?._id && !order?.cashfreeOrderId) {
            toast.error('No order ID on this row — cannot verify.');
            return;
        }
        if (verifyingCashfreeId) return;

        setVerifyingCashfreeId(order._id);
        try {
            const result = await verifyCashfreePaymentAdmin({
                orderId: order._id,
                cashfreeOrderId: order.cashfreeOrderId,
            });
            const { type, text } = getCashfreeVerifyMessage(result);

            if (type === 'success') {
                toast.success(text);
                const refreshedOrders = await getData();
                if (viewOrder?._id === order._id && refreshedOrders?.length) {
                    const refreshed = refreshedOrders.find((row) => row._id === order._id);
                    if (refreshed) setViewOrder(refreshed);
                }
            } else if (type === 'warning') {
                toast.warning(text);
            } else {
                toast.error(text);
            }
        } catch (error) {
            const { type, text } = getCashfreeVerifyMessage(null, error);
            if (type === 'warning') toast.warning(text);
            else toast.error(text);
        } finally {
            setVerifyingCashfreeId(null);
        }
    };

    const handleCreateShipment = async (order) => {
        if (!order?._id) {
            toast.error('Invalid order selected');
            return;
        }

        if (order.shipcluesOrderId || order.shipcluesShipmentId || createdShipments[order._id]) {
            toast.info('A Shipclues shipment is already recorded for this order.');
            return;
        }

        if (creatingShipmentId) return;

        setCreatingShipmentId(order._id);

        try {
            const response = await createShipcluesShipmentFromOrder(order, user?.token);
            const ids = readShipcluesRecordIds(response);

            if (!ids.shipcluesOrderId && !ids.shipcluesShipmentId) {
                toast.warning('Shipclues accepted the shipment, but the response had no order_id or shipment_id to save.');
                return;
            }

            const recorded = await recordAdminShipment(order._id, ids, user?.token);
            if (recorded.status === 404) {
                toast.warning('Shipclues created the shipment. Careveli cannot store the ids yet because the shipment route is not deployed.');
                setCreatedShipments(prev => ({ ...prev, [order._id]: ids }));
                return;
            }
            if (recorded.status === 409 || recorded.success === false) {
                toast.error(recorded.message || 'Could not record the shipment');
                return;
            }

            patchOrderInList(order._id, {
                shipcluesOrderId: ids.shipcluesOrderId || null,
                shipcluesShipmentId: ids.shipcluesShipmentId || null,
            });
            setCreatedShipments(prev => ({ ...prev, [order._id]: ids }));
            toast.success(recorded.message || response?.message || 'Shipment recorded');
        } catch (error) {
            console.error('Shipclues shipment error:', error);
            toast.error(getShipcluesErrorMessage(error));
        } finally {
            setCreatingShipmentId(null);
        }
    };

    const handleStatusUpdate = async () => {
        if (!statusUpdate.newStatus) {
            toast.error('Please select a status');
            return;
        }

        if (statusUpdate.newStatus === statusUpdate.currentStatus) {
            toast.info('Status is already set to ' + statusUpdate.newStatus);
            setStatusUpdate({ show: false, orderId: null, currentStatus: '', newStatus: '' });
            return;
        }

        setLoading(true);
        try {
            const response = await updateOrderStatus(
                statusUpdate.orderId,
                statusUpdate.newStatus,
                user?.token
            );

            if (response?.success) {
                toast.success(response.message || 'Order status updated successfully');
                const updated = response.data || {};
                patchOrderInList(statusUpdate.orderId, {
                    status: statusUpdate.newStatus,
                    ...(updated.paymentStatus != null ? { paymentStatus: updated.paymentStatus } : {}),
                    ...(updated.payment != null ? { payment: updated.payment } : {}),
                    ...(updated.orderNumber ? { orderNumber: updated.orderNumber } : {}),
                });
                setStatusUpdate({ show: false, orderId: null, currentStatus: '', newStatus: '' });
                getData();
            } else {
                toast.error(response?.message || 'Failed to update order status');
            }
        } catch (error) {
            console.error('Error updating order status:', error);
            toast.error('Error updating order status');
        } finally {
            setLoading(false);
        }
    };

    const handleRowSelected = React.useCallback(state => {
        setSelectedRows(state.selectedRows);
    }, []);

    const clearSelection = () => {
        setSelectedRows([]);
        setToggleCleared(prev => !prev);
    };

    const handleDeleteClick = (orderIds, label) => {
        const ids = Array.isArray(orderIds) ? orderIds.filter(Boolean) : [orderIds].filter(Boolean);
        if (!ids.length) return;

        const displayLabel = Array.isArray(label)
            ? `${label.length} order(s)`
            : label || `${ids.length} order(s)`;

        setDeleteModal({
            show: true,
            orderIds: ids,
            label: displayLabel
        });
    };

    const handleBulkDeleteClick = () => {
        if (!selectedRows.length) {
            toast.warning('No orders selected');
            return;
        }
        handleDeleteClick(
            selectedRows.map(row => row._id),
            `${selectedRows.length} order(s)`
        );
    };

    const handleBulkStatusOpen = () => {
        if (!selectedRows.length) {
            toast.warning('No orders selected');
            return;
        }
        setBulkStatusUpdate({ show: true, newStatus: 'Pending' });
    };

    const handleDeleteConfirm = async () => {
        if (!deleteModal.orderIds.length) return;

        setLoading(true);
        try {
            const { successCount, failCount } = await deleteOrders(deleteModal.orderIds, user?.token);

            if (successCount > 0) {
                toast.success(`Deleted ${successCount} order(s) successfully`);
                setDeleteModal({ show: false, orderIds: [], label: '' });
                clearSelection();
                getData();
            }
            if (failCount > 0) {
                toast.error(`Failed to delete ${failCount} order(s)`);
            }
        } catch (error) {
            console.error('Error deleting orders:', error);
            toast.error('Error deleting orders');
        } finally {
            setLoading(false);
        }
    };

    const handleBulkStatusUpdate = async () => {
        if (!bulkStatusUpdate.newStatus) {
            toast.error('Please select a status');
            return;
        }

        const orderIds = selectedRows.map(row => row._id).filter(Boolean);
        if (!orderIds.length) {
            toast.warning('No orders selected');
            return;
        }

        setLoading(true);
        try {
            const { successCount, failCount } = await updateBulkOrderStatus(
                orderIds,
                bulkStatusUpdate.newStatus,
                user?.token
            );

            if (successCount > 0) {
                toast.success(`Updated status for ${successCount} order(s)`);
                setBulkStatusUpdate({ show: false, newStatus: 'Pending' });
                clearSelection();
                getData();
            }
            if (failCount > 0) {
                toast.error(`Failed to update ${failCount} order(s)`);
            }
        } catch (error) {
            console.error('Error updating bulk order status:', error);
            toast.error('Error updating order status');
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
            page: key !== 'page' ? 1 : value // Reset to page 1 when filters change
        }));
    };

    const handleResetFilters = () => {
        setFilters({
            page: 1,
            limit: 20,
            search: '',
            status: '',
            paymentStatus: '',
            paymentMethod: '',
            startDate: '',
            endDate: ''
        });
    };

    const applyCashfreePendingQueue = () => {
        setFilters((prev) => ({
            ...prev,
            page: 1,
            paymentStatus: 'Pending',
            paymentMethod: 'Cashfree',
        }));
    };

    const unverifiedCashfreeCount = data.filter(isCashfreeUnverifiedPayment).length;

    const handlePageChange = (page) => {
        handleFilterChange('page', page);
    };

    const handlePerRowsChange = (newPerPage, page) => {
        setFilters(prev => ({
            ...prev,
            limit: newPerPage,
            page: page
        }));
    };

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="main-page-header d-md-flex align-items-center justify-content-between">
                    <div>
                        <ol className="breadcrumb fs-sm mb-1">
                            <li className="breadcrumb-item"><a href="#">Dashboard</a></li>
                            <li className="breadcrumb-item active" aria-current="page">Order Management</li>
                        </ol>
                        <h4 className="main-title mb-0">Order Management</h4>
                    </div>
                </div>

                {/* Statistics Cards */}
                <Row className="g-3 mb-4">
                    {[
                        {
                            label: 'Total Orders',
                            value: statistics.totalOrders ?? pagination.totalOrders ?? data.length,
                            subtext: null,
                            icon: 'ri-shopping-cart-2-line',
                            bg: '#FFEDD5',
                            color: '#EA580C',
                            accent: '#EA580C',
                        },
                        {
                            label: 'Total Revenue',
                            value: formatCurrency(statistics.totalRevenue),
                            subtext: statistics.cancelledAmount > 0
                                ? `${statistics.cancelledOrders} cancelled (${formatCurrency(statistics.cancelledAmount)}) excluded from GMV`
                                : 'GMV · excludes Cancelled, Refunded, Returned',
                            icon: 'ri-coins-line',
                            bg: '#DCFCE7',
                            color: '#16A34A',
                            accent: '#16A34A',
                        },
                        {
                            label: 'Average Order Value',
                            value: formatCurrency(statistics.averageOrderValue),
                            subtext: statistics.gmvOrders
                                ? `GMV ÷ ${statistics.gmvOrders} qualifying orders`
                                : null,
                            icon: 'ri-line-chart-line',
                            bg: '#DBEAFE',
                            color: '#2563EB',
                            accent: '#2563EB',
                        },
                    ].map(stat => (
                        <Col md={4} key={stat.label}>
                            <Card className="card-one order-stat-card shadow-sm" style={{ '--order-stat-accent': stat.accent }}>
                                <CardBody className="d-flex align-items-center justify-content-between gap-2">
                                    <div>
                                        <Label className="card-label fs-sm fw-medium mb-1 text-secondary">{stat.label}</Label>
                                        <h3 className="card-value mb-0">{stat.value}</h3>
                                        {stat.subtext ? (
                                            <small className="text-muted d-block mt-1" style={{ fontSize: '11px', lineHeight: 1.3 }}>
                                                {stat.subtext}
                                            </small>
                                        ) : null}
                                    </div>
                                    <div className="order-stat-card__icon" style={{ backgroundColor: stat.bg, color: stat.color }}>
                                        <i className={stat.icon} />
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    ))}
                </Row>

                {/* Filters */}
                <Card className="card-one mb-3">
                    <CardBody>
                        <h6 className="mb-3 fw-semibold">
                            <i className="ri-filter-3-line me-1" />
                            Filters
                        </h6>
                        <Row className="g-3">
                            {/* Search */}
                            <Col md={3}>
                                <Label>Search</Label>
                                <Input
                                    type="text"
                                    placeholder="Order #, SKU, Name, Email, Phone..."
                                    value={filters.search}
                                    onChange={(e) => handleFilterChange('search', e.target.value)}
                                />
                            </Col>

                            {/* Status Filter */}
                            <Col md={3}>
                                <Label>Order Status</Label>
                                <Input
                                    type="select"
                                    value={filters.status}
                                    onChange={(e) => handleFilterChange('status', e.target.value)}
                                >
                                    <option value="">All Statuses</option>
                                    {orderStatuses.map(status => (
                                        <option key={status} value={status}>{status}</option>
                                    ))}
                                </Input>
                            </Col>

                            <Col md={3}>
                                <Label>Payment Status</Label>
                                <Input
                                    type="select"
                                    value={filters.paymentStatus}
                                    onChange={(e) => handleFilterChange('paymentStatus', e.target.value)}
                                >
                                    <option value="">All payment statuses</option>
                                    {paymentStatuses.map(status => (
                                        <option key={status} value={status}>{status}</option>
                                    ))}
                                </Input>
                            </Col>

                            <Col md={3}>
                                <Label>Payment Method</Label>
                                <Input
                                    type="select"
                                    value={filters.paymentMethod}
                                    onChange={(e) => handleFilterChange('paymentMethod', e.target.value)}
                                >
                                    <option value="">All methods</option>
                                    {paymentMethods.map(method => (
                                        <option key={method} value={method}>{method}</option>
                                    ))}
                                </Input>
                            </Col>

                            {/* Start Date */}
                            <Col md={3}>
                                <Label>Start Date</Label>
                                <Input
                                    type="date"
                                    value={filters.startDate}
                                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                                />
                            </Col>

                            {/* End Date */}
                            <Col md={3}>
                                <Label>End Date</Label>
                                <Input
                                    type="date"
                                    value={filters.endDate}
                                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                                />
                            </Col>
                        </Row>

                        <div className="mt-3 d-flex flex-wrap gap-2 align-items-center">
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={handleResetFilters}
                            >
                                <i className="ri-restart-line"></i> Reset Filters
                            </Button>
                            <Button
                                variant="outline-warning"
                                size="sm"
                                onClick={applyCashfreePendingQueue}
                            >
                                <i className="ri-shield-check-line me-1"></i>
                                Cashfree verification queue
                            </Button>
                            {unverifiedCashfreeCount > 0 && (
                                <span className="text-danger small">
                                    <i className="ri-error-warning-line me-1" />
                                    {unverifiedCashfreeCount} unverified Cashfree payment(s) on this page
                                </span>
                            )}
                        </div>
                    </CardBody>
                </Card>

                {/* Orders Table */}
                <Card className="card-one orders-table-card">
                    <CardBody className="orders-table-card__body">
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                            <CardTitle tag="h6" className="mb-0">
                                <i className="ri-list-check me-1" />
                                All Orders ({pagination.totalOrders || data.length})
                            </CardTitle>
                            {selectedRows.length > 0 && (
                                <div className="d-flex gap-2 flex-wrap">
                                    <Button variant="primary" size="sm" onClick={handleBulkStatusOpen}>
                                        <i className="ri-refresh-line me-1"></i>
                                        Update Status ({selectedRows.length})
                                    </Button>
                                    <Button variant="danger" size="sm" onClick={handleBulkDeleteClick}>
                                        <i className="ri-delete-bin-line me-1"></i>
                                        Delete ({selectedRows.length})
                                    </Button>
                                </div>
                            )}
                        </div>

                        <p className="text-muted small mb-2 orders-table-scroll-hint">
                            <i className="ri-arrow-left-right-line me-1" />
                            Scroll horizontally to see Payment and Actions
                        </p>

                        <div className="admin-datatable-scroll orders-datatable-wrap">
                            <DataTable
                                columns={columns}
                                data={data}
                                pagination
                                paginationServer
                                paginationTotalRows={pagination.totalOrders}
                                paginationDefaultPage={filters.page}
                                paginationPerPage={filters.limit}
                                onChangePage={handlePageChange}
                                onChangeRowsPerPage={handlePerRowsChange}
                                progressPending={loading}
                                progressComponent={<DataTableSkeleton rows={8} columns={7} />}
                                selectableRows
                                selectableRowsHighlight
                                onSelectedRowsChange={handleRowSelected}
                                clearSelectedRows={toggleCleared}
                                customStyles={dataTableWideStyles}
                                paginationComponentOptions={dataTablePaginationOptions}
                                highlightOnHover
                                striped
                                noDataComponent={
                                    <AdminEmptyState
                                        icon="ri-shopping-bag-3-line"
                                        title="No orders found"
                                        description="Orders matching your filters will appear here."
                                    />
                                }
                            />
                        </div>
                    </CardBody>
                </Card>

                {/* Status Update Modal */}
                {statusUpdate.show && (
                    <ConfirmModal
                        show={statusUpdate.show}
                        onCloseClick={() => setStatusUpdate({ show: false, orderId: null, currentStatus: '', newStatus: '' })}
                        onConfirm={handleStatusUpdate}
                        data={{
                            actionType: 'OrderStatusUpdate',
                            currentStatus: statusUpdate.currentStatus,
                            newStatus: statusUpdate.newStatus,
                            setNewStatus: (val) => setStatusUpdate(prev => ({ ...prev, newStatus: val })),
                            orderStatuses: orderStatuses
                        }}
                    />
                )}

                {bulkStatusUpdate.show && (
                    <ConfirmModal
                        show={bulkStatusUpdate.show}
                        onCloseClick={() => setBulkStatusUpdate({ show: false, newStatus: 'Pending' })}
                        onConfirm={handleBulkStatusUpdate}
                        data={{
                            actionType: 'OrderBulkStatusUpdate',
                            newStatus: bulkStatusUpdate.newStatus,
                            setNewStatus: (val) => setBulkStatusUpdate(prev => ({ ...prev, newStatus: val })),
                            orderStatuses: orderStatuses,
                            selectedCount: selectedRows.length
                        }}
                    />
                )}

                {deleteModal.show && (
                    <ConfirmModal
                        show={deleteModal.show}
                        onCloseClick={() => setDeleteModal({ show: false, orderIds: [], label: '' })}
                        onConfirm={handleDeleteConfirm}
                        data={{
                            actionType: 'Delete',
                            message: `Delete ${deleteModal.label}? This action cannot be undone.`
                        }}
                    />
                )}

                <Modal isOpen={Boolean(viewOrder)} toggle={() => setViewOrder(null)} size="lg" centered>
                    <ModalHeader toggle={() => setViewOrder(null)}>
                        Order Details — {viewOrder?.orderNumber}
                    </ModalHeader>
                    <ModalBody>
                        {viewOrderLoading ? (
                            <div className="text-center py-4">
                                <span className="spinner-border text-primary" role="status" aria-hidden="true" />
                                <p className="text-muted mt-2 mb-0">Loading order details…</p>
                            </div>
                        ) : viewOrder ? (
                            <OrderDetailsContent
                                data={viewOrder}
                                onVerifyCashfree={handleVerifyCashfreePayment}
                                verifyingCashfreeId={verifyingCashfreeId}
                                onDownloadInvoice={handleDownloadInvoice}
                                onIssueInvoice={handleIssueInvoice}
                                downloadingInvoiceId={downloadingInvoiceId}
                                issuingInvoiceId={issuingInvoiceId}
                            />
                        ) : null}
                    </ModalBody>
                </Modal>

                <Footer />
            </div>
        </>
    );
}

export default Order;
